import React, { forwardRef, useState } from 'react';
import { Check } from 'lucide-react';
import styles from './Checkbox.module.scss';
import { useInputCommon } from '../../hooks';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
    /** Whether the checkbox is in an error state */
    error?: boolean;
    /** Label text for the checkbox */
    label?: string;
    /** Helper text displayed below the checkbox */
    helperText?: string;
    /** Error text displayed when error is true */
    errorText?: string;
    /** Size of the checkbox */
    size?: 'sm' | 'md' | 'lg';
    /** Custom className for the container */
    className?: string;
    /** Custom className for the checkbox input */
    checkboxClassName?: string;
    /** Custom className for the label */
    labelClassName?: string;
    /** Whether the checkbox is in an indeterminate state */
    indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    (
        {
            checked,
            defaultChecked,
            disabled = false,
            error = false,
            label,
            helperText,
            errorText,
            size = 'md',
            className = '',
            checkboxClassName = '',
            labelClassName = '',
            onChange,
            onClick,
            onFocus,
            onBlur,
            name,
            value,
            id,
            required = false,
            indeterminate = false,
            ...props
        },
        ref
    ) => {
        const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

        const [focused, setFocused] = useState(false);

        // Internal state for uncontrolled mode
        const [internalChecked, setInternalChecked] = useState(defaultChecked || false);

        // Determine if this is a controlled or uncontrolled component
        const isControlled = checked !== undefined;
        const isUncontrolled = !isControlled && !onChange && !onClick;

        // Get the current checked state
        const currentChecked = isControlled ? checked : isUncontrolled ? internalChecked : checked;

        const { labelElement, errorTextElement, helperTextElement, containerClasses } = useInputCommon({
            id: checkboxId,
            label,
            error,
            errorText,
            helperText,
            required,
            disabled,
            className,
            labelClassName,
            inputType: 'checkbox',
        });

        // Handle indeterminate state
        const handleRef = (element: HTMLInputElement | null) => {
            if (element) {
                element.indeterminate = indeterminate;
            }
            if (typeof ref === 'function') {
                ref(element);
            } else if (ref) {
                ref.current = element;
            }
        };

        // Handle change for uncontrolled mode
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            if (isUncontrolled) {
                setInternalChecked(event.target.checked);
            }
            onChange?.(event);
        };

        // Handle click for uncontrolled mode
        const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
            onClick?.(event);
        };

        const checkboxClasses = [
            styles.checkbox,
            styles[`size-${size}`],
            error ? styles.error : '',
            disabled ? styles.disabled : '',
            indeterminate ? styles.indeterminate : '',
            currentChecked ? styles.checked : '',
            focused ? styles.focused : '',
            checkboxClassName,
        ]
            .filter(Boolean)
            .join(' ');

        const wrapperClasses = [styles.checkboxWrapper, disabled ? styles.disabled : ''].filter(Boolean).join(' ');

        return (
            <div className={containerClasses}>
                <div className={wrapperClasses}>
                    <div className={styles.checkboxInputWrapper}>
                        <input
                            ref={handleRef}
                            type='checkbox'
                            id={checkboxId}
                            name={name}
                            value={value}
                            checked={currentChecked}
                            defaultChecked={isUncontrolled ? undefined : defaultChecked}
                            disabled={disabled}
                            required={required}
                            onChange={handleChange}
                            onClick={handleClick}
                            onFocus={(e) => {
                                setFocused(true);
                                onFocus?.(e);
                            }}
                            onBlur={(e) => {
                                setFocused(false);
                                onBlur?.(e);
                            }}
                            className={styles.checkboxInput}
                            aria-invalid={error}
                            aria-describedby={
                                error && errorText
                                    ? `${checkboxId}-error`
                                    : helperText
                                      ? `${checkboxId}-helper`
                                      : undefined
                            }
                            {...props}
                        />
                        <div className={checkboxClasses}>
                            <div className={styles.checkboxIcon}>
                                {indeterminate ? (
                                    <div className={styles.indeterminateIcon} />
                                ) : (
                                    <Check size={size === 'sm' ? 12 : size === 'lg' ? 18 : 14} />
                                )}
                            </div>
                        </div>
                    </div>

                    {labelElement}
                </div>

                {errorTextElement}

                {helperTextElement}
            </div>
        );
    }
);

Checkbox.displayName = 'Checkbox';
